import { c as _c } from "react/compiler-runtime";
import { IcoEyeCrossed16 } from '@onefootprint/icons';
import { Divider, Form, Tooltip } from '@onefootprint/ui';
import { useId } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import { CelNodeFormSubtitle } from '../../../shared/cel-node-form-instructions';
import ExpandableExpressionEditor from '../../../shared/expandable-expression-editor';
import type { FormData } from './selfie.types';
type FaceComparisonSectionProps = {
  nodeId: string;
  onToggle: (checked: boolean) => void;
};
const FaceComparisonSection = t0 => {
  const $ = _c(38);
  const {
    nodeId,
    onToggle
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "selfie.faceComparison"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const config = useGetNodeConfigContext(nodeId, "templatable");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "compareFaces"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isEnabled = useWatch(t2);
  const thresholdId = useId();
  let t3;
  if ($[2] !== t) {
    t3 = t(_temp);
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="text-label-2 text-primary">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t(_temp2);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex-shrink-0 text-tertiary"><IcoEyeCrossed16 /></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <Tooltip content={t5} side="left">{t6}</Tooltip>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t7) {
    t8 = <div className="flex items-center justify-between gap-2">{t4}{t7}</div>;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t(_temp3);
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <span className="text-body-2 text-secondary">{t9}</span>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <div className="flex flex-col gap-1">{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t(_temp4);
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== onToggle || $[24] !== t12) {
    t13 = <Form.Toggle name="compareFaces" label={t12} size="compact" onChange={onToggle} />;
    $[23] = onToggle;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== config?.autocomplete || $[27] !== config?.languageDefinition || $[28] !== isEnabled || $[29] !== t || $[30] !== thresholdId) {
    t14 = isEnabled && <><Divider /><fieldset aria-label={t(_temp5)} className="flex items-center gap-3"><div className="flex-1 min-w-0 flex items-center min-h-8 text-label-3 text-primary">{t(_temp6)}</div><div className="flex-1 min-w-0"><Controller name="reference" rules={{
            required: t(_temp7)
          }} render={t15 => {
            const {
              field,
              fieldState
            } = t15;
            return <div className="flex flex-col gap-1"><ExpandableExpressionEditor value={field.value} onChange={field.onChange} disabled={field.disabled} autocompleteItems={config?.autocomplete} languageDefinition={config?.languageDefinition} placeholder={t(_temp8)} hasError={!!fieldState.error} labels={{
                expand: t(_temp9),
                dialogTitle: t(_temp10),
                subtitle: <CelNodeFormSubtitle i18nKey={_temp11} />,
                celSubtitle: t(_temp12),
                instructions: t(_temp13)
              }} />{fieldState.error && <span className="text-error text-caption-2">{fieldState.error.message}</span>}</div>;
          }} /></div></fieldset><div className="flex items-start gap-3"><label htmlFor={thresholdId} className="flex-1 min-w-0 flex items-center min-h-8 text-label-3 text-primary">{t(_temp14)}</label><div className="flex-1 min-w-0"><Form.Number id={thresholdId} name="similarityThreshold" hint={t(_temp15)} size="compact" allowNegative={false} thousandsSeparator={false} rules={{
            required: t(_temp16),
            min: {
              value: 0,
              message: t(_temp17)
            },
            max: {
              value: 100,
              message: t(_temp18)
            }
          }} /></div></div></>;
    $[26] = config?.autocomplete;
    $[27] = config?.languageDefinition;
    $[28] = isEnabled;
    $[29] = t;
    $[30] = thresholdId;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15) {
    t16 = <section className="flex flex-col gap-3">{t11}{t15}</section>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
};
export default FaceComparisonSection;
function _temp($) {
  return $.title;
}
function _temp2($_0) {
  return $_0.employeeOnly;
}
function _temp3($_1) {
  return $_1.subtitle;
}
function _temp4($_2) {
  return $_2.toggle;
}
function _temp5($_3) {
  return $_3.reference;
}
function _temp6($_4) {
  return $_4.reference;
}
function _temp7($_5) {
  return $_5.expression.required;
}
function _temp8($_6) {
  return $_6.expression.placeholder;
}
function _temp9($_7) {
  return $_7.expression.expand;
}
function _temp10($_8) {
  return $_8.reference;
}
function _temp11($_9) {
  return $_9.selfie.faceComparison.expression.subtitle;
}
function _temp12($_10) {
  return $_10.expression.celSubtitle;
}
function _temp13($_11) {
  return $_11.expression.instructions;
}
function _temp14($_12) {
  return $_12.threshold.label;
}
function _temp15($_13) {
  return $_13.threshold.hint;
}
function _temp16($_14) {
  return $_14.threshold.rangeError;
}
function _temp17($_15) {
  return $_15.threshold.rangeError;
}
function _temp18($_16) {
  return $_16.threshold.rangeError;
}